/**
 * Этап «Доставка и оплата» (раздел 14 требований): правила документов, задачи на оплату, сроки.
 * Чистые функции — используются и сервером (проверки, PDF), и порталом (подсветка сроков).
 */
import type { LotStatus, TransportKind, WinnerDocType } from "./enums";

/** Срок подачи на оплату: подтверждение доставки + 15 календарных дней (14.2). */
export const PAYMENT_DUE_DAYS = 15;
const DAY_MS = 86_400_000;

export const paymentDueAt = (confirmedAt: Date): Date => new Date(confirmedAt.getTime() + PAYMENT_DUE_DAYS * DAY_MS);

/**
 * Сколько полных дней осталось до срока (отрицательное — просрочка).
 * Считаем по календарным дням: срок «до 06.11» означает весь день 06.11.
 */
export function daysLeft(dueAt: Date | string, now: Date | number = Date.now()): number {
  const due = new Date(dueAt).getTime();
  const n = typeof now === "number" ? now : now.getTime();
  return Math.ceil((due - n) / DAY_MS);
}

/** «осталось 3 дня» / «срок сегодня» / «просрочено на 2 дня». */
export function daysLeftLabel(days: number): string {
  if (days > 0) return `осталось ${days} ${pluralDays(days)}`;
  if (days === 0) return "срок сегодня";
  const over = -days;
  return `просрочено на ${over} ${pluralDays(over)}`;
}

export function pluralDays(n: number): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return "дней";
  if (last === 1) return "день";
  if (last >= 2 && last <= 4) return "дня";
  return "дней";
}

/**
 * Когда талон прохождения границы обязателен для подтверждения доставки (открытый вопрос 14.1):
 * по умолчанию — только для международных перевозок.
 */
export const BORDER_PASS_RULES = ["international", "always", "never"] as const;
export type BorderPassRule = (typeof BORDER_PASS_RULES)[number];
export const DEFAULT_BORDER_PASS_RULE: BorderPassRule = "international";

/** Документы доставки, без которых логист не может подтвердить доставку (14.2). */
export function requiredDeliveryDocs(kind: TransportKind, rule: BorderPassRule = DEFAULT_BORDER_PASS_RULE): WinnerDocType[] {
  const docs: WinnerDocType[] = ["cmr"];
  if (rule === "always" || (rule === "international" && kind === "international")) docs.push("border_pass");
  docs.push("invoice");
  return docs;
}

export function missingDeliveryDocs(required: WinnerDocType[], present: Iterable<WinnerDocType>): WinnerDocType[] {
  const have = new Set(present);
  return required.filter((t) => !have.has(t));
}

/** Документы, которые бот запрашивает у перевозчика после кнопки «Доставка завершена» (в этом порядке). */
export const DELIVERY_BOT_STEPS: readonly WinnerDocType[] = ["cmr", "border_pass", "invoice"];

/** Статусы, в которых перевозчик может нажать «Доставка завершена» (delivered — повторно, после «Вернуть на доработку»). */
export const DELIVERY_REPORTABLE_STATUSES: readonly LotStatus[] = ["awaiting_winner_data", "formalized_pre_trip", "delivered"];
/** Статусы, в которых логист может подтвердить доставку (в т.ч. если перевозчик передал документы мимо бота). */
export const DELIVERY_CONFIRMABLE_STATUSES: readonly LotStatus[] = ["awaiting_winner_data", "formalized_pre_trip", "delivered"];
/** Статусы, в которых в карточке показывается блок «Доставка». */
export const DELIVERY_STAGE_STATUSES: readonly LotStatus[] = ["awaiting_winner_data", "formalized_pre_trip", "delivered", "closed", "paid"];

/** Задача на оплату (таблица payment_tasks). Даты — ISO-строки. */
export type PaymentTask = {
  id: string;
  lotId: string;
  confirmedAt: string;
  dueAt: string;
  paidAt: string | null;
  paidBy: string | null;
  createdBy: string;
  pdfGeneratedAt: string | null;
  createdAt: string;
};

/** Строка блока «К оплате» на главной (GET /api/payment-tasks). */
export type PaymentTaskRow = PaymentTask & {
  lotNumber: string;
  lotStatus: LotStatus;
  lotCreatedBy: string;
  lotCreatedByName: string;
  transportKind: TransportKind;
  loadingAddress: string;
  unloadingAddress: string;
  carrierName: string | null;
  amount: number | null;
  confirmedByName: string | null;
  paidByName: string | null;
};

/** Части календарной даты момента в поясе tz. */
function calendarParts(d: Date, tz: string) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(d);
  const get = (t: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return Date.UTC(get("year"), get("month") - 1, get("day"));
}

/**
 * Разница в календарных днях между днём срока и «сегодня» в поясе tz: 3 — срок через три дня,
 * 0 — сегодня, −2 — просрочено на два дня. Для напоминаний логисту (14.3.5).
 */
export function calendarDaysUntil(dueAt: Date | string, now: Date | number = Date.now(), tz = "Asia/Almaty"): number {
  const n = typeof now === "number" ? new Date(now) : now;
  return Math.round((calendarParts(new Date(dueAt), tz) - calendarParts(n, tz)) / DAY_MS);
}

/** За сколько дней до срока напоминать логисту. */
export const PAYMENT_REMINDER_DAYS_BEFORE = 3;
